import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { API_BASE_URL } from "./config";
import Header from "./components/Header";
import Footer from "./components/Footer";
import SignInPage from "./components/SignInPage";
import SignUpPage from "./components/SignUpPage";
import ProductList from "./components/ProductList";
import AddProductForm from "./components/AddProductForm";
import EditProductForm from "./components/EditProductForm";
import LogoutPage from "./components/LogoutPage";
import NotFound from "./components/NotFound";
import SellerForm from "./components/SellerForm";
import ProductManager from "./components/ProductManager";
import About from "./components/About";
import ProductDetails from "./components/ProductDetails";
import ViewProductForm from "./components/ViewProductForm";
import ShoppingCart from "./components/ShoppingCart";
import Profile from "./components/Profile";
import Settings from "./components/Settings";
import Orders from "./components/Orders";
import AddressBook from "./components/AddressBook";
import Checkout from "./components/Checkout";
import Home from "./components/Home";
import ShopPage from "./components/ShopPage";
import ShopVerification from "./components/ShopVerification";
import AdminDashboard from "./components/AdminDashboard";
import AdminUserManagement from "./components/AdminUserManagement";
import AdminProductManagement from "./components/AdminProductManagement";
import Wishlist from "./components/Wishlist";

// Custom hook สำหรับการตรวจสอบ session
const useCheckSession = () => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          // Redirect to SignIn if no token
          setIsAuthenticated(false);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/auth/check-session`,
          {
            headers: {
              Authorization: token,
            },
          }
        );

        if (!response.ok) {
          // Redirect to SignIn if session is invalid
          setIsAuthenticated(false);
          return;
        }

        setIsAuthenticated(true);
      } catch (error) {
        console.error("Error checking session:", error);
        setIsAuthenticated(false);
      }
    };

    checkSession();
  }, [location]);

  return isAuthenticated;
};

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useCheckSession();

  if (isAuthenticated === null) {
    // Still checking authentication status
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

const AppContent = () => {
  const isAuthenticated = useCheckSession();

  return (
    <>
      {isAuthenticated && <Header />}
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/home" element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        } />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route
          path="/for-you"
          element={
            <ProtectedRoute>
              <ProductList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/all-products"
          element={
            <ProtectedRoute>
              <ProductList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/product-details/:productId"
          element={
            <ProtectedRoute>
              <ProductDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/about"
          element={
            <ProtectedRoute>
              <About />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-cart"
          element={
            <ProtectedRoute>
              <ShoppingCart />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/orders"
          element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/addresses"
          element={
            <ProtectedRoute>
              <AddressBook />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/wishlist"
          element={
            <ProtectedRoute>
              <Wishlist />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/my-store"
          element={
            <ProtectedRoute>
              <ProductManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/seller-form"
          element={
            <ProtectedRoute>
              <SellerForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/my-store/add-product"
          element={
            <ProtectedRoute>
              <AddProductForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/my-store/view-product/:productId"
          element={
            <ProtectedRoute>
              <ViewProductForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/my-store/edit-product/:productId"
          element={
            <ProtectedRoute>
              <EditProductForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/logout"
          element={
            <ProtectedRoute>
              <LogoutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop/:sellerId"
          element={
            <ProtectedRoute>
              <ShopPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/verify-shops"
          element={
            <ProtectedRoute>
              <ShopVerification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminUserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/products"
          element={
            <ProtectedRoute>
              <AdminProductManagement />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {isAuthenticated && <Footer />}
    </>
  );
};

export default App;